// src/components/PayoutsList.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import produce from "immer";

const PayoutsList = ({
  setCheckedDocs,
  handleCheckbox,
  checkedDocs,
  globalCheckedDocs,
  categoryCode = "socvyplaty",
  loadMeasures,
}) => {
  const [measures, setMeasures] = useState([]);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [listScrollPosition, setListScrollPosition] = useState(0);
  const [checklistScrollPosition, setChecklistScrollPosition] = useState(0);
  const listRef = useRef(null);
  const checklistRef = useRef(null);
  const { measureId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const loadAndOpen = async () => {
      try {
        const data = await loadMeasures(categoryCode);
        setMeasures(data);

        if (measureId) {
          const targetMeasure = data.find((m) => m.id === parseInt(measureId));
          if (targetMeasure) {
            setSelectedMeasure(targetMeasure);
          } else {
            console.warn(
              "Мера с ID",
              measureId,
              "не найдена в категории",
              categoryCode
            );
          }
        }
      } catch (err) {
        console.error("Ошибка:", err);
        alert("Не удалось загрузить меры.");
      }
    };

    loadAndOpen();
  }, [categoryCode, loadMeasures, measureId]);

    // Сброс selectedMeasure, когда measureId исчезает из URL (нажали "назад к списку")
  useEffect(() => {
    if (!measureId && selectedMeasure) {
      setSelectedMeasure(null);
      // Восстанавливаем скролл списка, если нужно
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listScrollPosition;
        }
      }, 0);
    }
  }, [measureId, selectedMeasure, listScrollPosition]);

  useEffect(() => {
    if (selectedMeasure && checklistRef.current) {
      checklistRef.current.scrollTop = checklistScrollPosition;
    }
  }, [selectedMeasure, checklistScrollPosition]);

  const openChecklist = (measure) => {
    if (listRef.current) setListScrollPosition(listRef.current.scrollTop);
    if (checklistRef.current) setChecklistScrollPosition(checklistRef.current.scrollTop);
    navigate(`/payouts/${measure.id}`);
  };

  const closeChecklist = () => {
    setSelectedMeasure(null);
    navigate("/payouts");
    setTimeout(() => {
      if (listRef.current) listRef.current.scrollTop = listScrollPosition;
    }, 0);
  };

  if (selectedMeasure) {
    const docs = selectedMeasure.measure_documents || [];
    const saved = checkedDocs[`checklist-${selectedMeasure.id}`] || [];
    const done = docs.filter(
      (d) =>
        saved.includes(d.document.code) ||
        globalCheckedDocs.has(d.document.code)
    ).length;
    const total = docs.length;
    const percent = total > 0 ? Math.round((done / total) * 100) : 100;

    const resetChecklist = () => {
      setCheckedDocs(produce((draft) => {
        docs.forEach((d) => {
          const docCode = d.document.code;
          Object.keys(draft).forEach((key) => {
            if (draft[key]) {
              draft[key] = draft[key].filter((code) => code !== docCode);
              if (draft[key].length === 0) delete draft[key];
            }
          });
        });
      }));
    };

    return (
      <div
        className="container checklist-view position-relative py-5 show"
        ref={checklistRef}
        style={{ overflowY: "auto", maxHeight: "100vh" }}
      >
        <div className="back-arrow-circle" onClick={closeChecklist}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="white"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
            />
          </svg>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div
              id={`measure-detail-${selectedMeasure.id}`}
              tabIndex="-1"
              className="card border-0 shadow-sm"
            >
              <div className="card-body">
                <h4 className="card-title text-primary mb-4">
                  {selectedMeasure.title}
                </h4>

                <p className="lead text-muted mb-4">
                  {selectedMeasure.short_desc || selectedMeasure.conditions || 'Описание отсутствует'}
                </p>

                {total > 0 && (
                  <>
                    <div className="progress-container mb-4">
                      <div className="progress">
                        <div
                          className="progress-bar bg-primary"
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                      <div className="progress-text">
                        {done} из {total} документов
                      </div>
                    </div>

                    <div className="docs-list">
                      <h5 className="text-primary mb-3">
                        Необходимые документы:
                      </h5>

                      <div className="d-flex justify-content-end mb-3">
                        <button
                          className="btn btn-outline-danger"
                          onClick={resetChecklist}
                          aria-label="Сбросить галочки в чек-листе для этой меры поддержки"
                        >
                          Сбросить галочки
                        </button>
                      </div>

                      <ul className="list-group list-group-flush">
                        {docs.map((d, index) => {
                          const doc = d.document;
                          const isChecked =
                            saved.includes(doc.code) ||
                            globalCheckedDocs.has(doc.code);
                          return (
                            <li key={index} className="list-group-item">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`doc-${selectedMeasure.id}-${doc.code}`}
                                  checked={isChecked}
                                  onChange={(e) =>
                                    handleCheckbox(
                                      selectedMeasure.id,
                                      doc.code,
                                      e.target.checked
                                    )
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`doc-${selectedMeasure.id}-${doc.code}`}
                                >
                                  {doc.name}
                                </label>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </>
                )}

                <section aria-labelledby="submission-methods" className="mt-5">
                  <h5 id="submission-methods" className="text-primary mb-4">
                    Способы получить услугу:
                  </h5>
                  <div className="d-flex flex-column flex-md-row gap-3 justify-content-center mb-4">
                    {selectedMeasure.gu_rf_link &&
                      selectedMeasure.gu_rf_link !== "nan" && (
                        <a
                          href={selectedMeasure.gu_rf_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                          aria-label="Подать заявление на Госуслугах (открывается в новой вкладке)"
                        >
                          Подать на Госуслугах
                        </a>
                      )}

                    {selectedMeasure.gu_lo_link &&
                      selectedMeasure.gu_lo_link !== "nan" && (
                        <a
                          href={selectedMeasure.gu_lo_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary"
                          aria-label="Перейти в МФЦ для записи на приём (открывается в новой вкладке)"
                        >
                          В МФЦ (записаться на приём)
                        </a>
                      )}

                    {selectedMeasure.branches_link &&
                      selectedMeasure.branches_link !== "nan" && (
                        <a
                          href={selectedMeasure.branches_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                          aria-label="Перейти на карту филиалов Соцзащиты для очной подачи документов (открывается в новой вкладке)"
                        >
                          В Соцзащиту (карта филиалов)
                        </a>
                      )}
                  </div>
                </section>

                <div className="mt-4 d-flex justify-content-center">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={closeChecklist}
                    aria-label="Вернуться к списку мер"
                  >
                    ← Назад к списку
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="container payouts-list position-relative py-5 show"
      ref={listRef}
      style={{ overflowY: "auto", maxHeight: "100vh" }}
    >
      <div className="back-arrow-circle" onClick={() => navigate('/')}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="white"
          viewBox="0 0 16 16"
        >
          <path
            fillRule="evenodd"
            d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
          />
        </svg>
      </div>

      <h2 className="text-center mb-5 fw-bold text-primary">
        Социальные выплаты
      </h2>

      <div className="row justify-content-center">
        <div className="col-lg-8">
          {(Array.isArray(measures) ? measures.length : 0) === 0 ? (
            <p className="text-center text-muted">Загрузка мер поддержки...</p>
          ) : (
            measures
              .filter(
                (measure) => measure.category?.toLowerCase() === "socvyplaty"
              )
              .map((measure) => (
                <div
                  key={measure.id}
                  id={`measure-${measure.id}`}
                  tabIndex="-1"
                  className="payout-card"
                  onClick={() => openChecklist(measure)}
                >
                  <div className="payout-title">{measure.title}</div>
                  {measure.amount && measure.amount !== "nan" && (
                    <div className="payout-amount">{measure.amount}</div>
                  )}
                  <p className="text-muted mt-2">
                    {measure.short_desc || measure.conditions}
                  </p>
                </div>
              ))
          )}

          {measures && measures.length > 0 ? null : (
  <p className="text-center text-muted">Нет доступных мер в этой категории.</p>
)}
        </div>
      </div>
    </div>
  );
};

export default PayoutsList;